import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext.hook';

interface SenderProfile {
  user_id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  role: string | null;
  avatar_url: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  partner: 'Escritório',
  assessor: 'Assessor',
  investidor: 'Investidor',
  incorporador: 'Incorporador',
  mfb: 'MFB',
  mfa: 'MFA',
  diligencias_manager: 'Gestor de Diligências',
};

function getSenderName(profile: SenderProfile | null | undefined): string {
  if (!profile) return 'Usuário';

  const fullName = [profile.first_name, profile.last_name].filter(Boolean).join(' ').trim();
  if (fullName) return fullName;
  if (profile.email?.trim()) return profile.email.trim();
  if (profile.role && ROLE_LABELS[profile.role]) return ROLE_LABELS[profile.role];
  return 'Usuário';
}

export interface DiligenciaMessageMetadata {
  file_path?: string;
  file_name?: string;
  file_size?: number;
  mime_type?: string;
  expires_at?: string;
  [key: string]: unknown;
}

export interface DiligenciaMessage {
  id: string;
  diligencia_id: string;
  sender_id: string;
  message: string;
  type: 'text' | 'system' | 'document';
  metadata: DiligenciaMessageMetadata;
  created_at: string;
  // Joined fields
  sender_name?: string;
  sender_avatar_url?: string | null;
}

export const useDiligenciaMessages = (diligenciaId: string | undefined) => {
  const [messages, setMessages] = useState<DiligenciaMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  // Cache do próprio perfil para optimistic updates sem round-trip.
  const selfProfileRef = useRef<{ name: string; avatarUrl: string | null } | null>(null);

  const enrichMessages = useCallback(async (rows: DiligenciaMessage[]): Promise<DiligenciaMessage[]> => {
    const senderIds = [...new Set(rows.map((m) => m.sender_id))];
    if (senderIds.length === 0) return rows;

    const { data: profiles } = await supabase
      .from('profiles')
      .select('user_id, first_name, last_name, email, role, avatar_url')
      .in('user_id', senderIds);

    const profilesMap = new Map<string, { name: string; avatarUrl: string | null }>();
    for (const p of profiles ?? []) {
      profilesMap.set(p.user_id, {
        name: getSenderName(p as SenderProfile),
        avatarUrl: p.avatar_url ?? null,
      });
    }

    if (user?.id) {
      const selfProfile = profilesMap.get(user.id);
      if (selfProfile) selfProfileRef.current = selfProfile;
    }

    return rows.map((m) => ({
      ...m,
      sender_name: profilesMap.get(m.sender_id)?.name ?? 'Usuário',
      sender_avatar_url: profilesMap.get(m.sender_id)?.avatarUrl ?? null,
    }));
  }, [user?.id]);

  const fetchMessages = useCallback(async () => {
    if (!diligenciaId) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('diligencia_messages')
        .select('*')
        .eq('diligencia_id', diligenciaId)
        .order('created_at', { ascending: true });

      if (error) throw error;

      const enriched = await enrichMessages((data ?? []) as DiligenciaMessage[]);
      setMessages(enriched);
    } catch (err) {
      console.error('[useDiligenciaMessages] fetch error:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar as mensagens.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [diligenciaId, enrichMessages, toast]);

  const sendMessage = async (
    message: string,
    type: 'text' | 'system' | 'document' = 'text',
    metadata: DiligenciaMessageMetadata = {}
  ): Promise<boolean> => {
    if (!diligenciaId || !user || !message.trim()) return false;

    setIsSending(true);
    try {
      const { data, error } = await supabase
        .from('diligencia_messages')
        .insert({
          diligencia_id: diligenciaId,
          sender_id: user.id,
          message: message.trim(),
          type,
          metadata,
        })
        .select('*')
        .single();

      if (error) throw error;

      // Optimistic update: aparece imediatamente para o remetente
      // (o subscribe também vai entregar, mas deduplicamos por id).
      if (data) {
        const self = selfProfileRef.current;
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.id)) return prev;
          return [
            ...prev,
            {
              ...(data as DiligenciaMessage),
              sender_name: self?.name ?? 'Você',
              sender_avatar_url: self?.avatarUrl ?? null,
            },
          ];
        });
      }

      return true;
    } catch (err) {
      console.error('[useDiligenciaMessages] send error:', err);
      toast({
        title: 'Erro',
        description: 'Não foi possível enviar a mensagem.',
        variant: 'destructive',
      });
      return false;
    } finally {
      setIsSending(false);
    }
  };

  // Fetch inicial — separado da subscription para não re-executar.
  useEffect(() => {
    if (!diligenciaId) return;
    fetchMessages();
  }, [diligenciaId, fetchMessages]);

  // Realtime subscription — depende APENAS de diligenciaId para evitar
  // re-subscribe em todo render (bug anterior perdia mensagens).
  useEffect(() => {
    if (!diligenciaId) return;

    let cancelled = false;

    const channel = supabase
      .channel(`diligencia-messages-${diligenciaId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'diligencia_messages',
          filter: `diligencia_id=eq.${diligenciaId}`,
        },
        async (payload) => {
          if (cancelled) return;
          const newMsg = payload.new as DiligenciaMessage;

          // Dedupe rápido antes do fetch do perfil
          let alreadyPresent = false;
          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) {
              alreadyPresent = true;
              return prev;
            }
            return prev;
          });
          if (alreadyPresent) return;

          const { data: profile } = await supabase
            .from('profiles')
            .select('user_id, first_name, last_name, email, role, avatar_url')
            .eq('user_id', newMsg.sender_id)
            .maybeSingle();

          if (cancelled) return;

          const senderName = getSenderName(profile as SenderProfile | null);
          const senderAvatarUrl = profile?.avatar_url ?? null;

          setMessages((prev) => {
            if (prev.some((m) => m.id === newMsg.id)) return prev;
            return [...prev, { ...newMsg, sender_name: senderName, sender_avatar_url: senderAvatarUrl }];
          });
        }
      )
      .subscribe((status, err) => {
        if (cancelled) return;
        setIsSubscribed(status === 'SUBSCRIBED');
        if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          console.warn(`[useDiligenciaMessages] channel status=${status}`, err ?? '');
        }
      });

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
      setIsSubscribed(false);
    };
  }, [diligenciaId]);

  return {
    messages,
    isLoading,
    isSending,
    isSubscribed,
    sendMessage,
    fetchMessages,
  };
};
